/**
 * Authentication Service
 * 
 * Wraps MSAL.js for Azure Entra External ID (CIAM) authentication.
 * Provides methods for login, logout, token acquisition, and user info extraction.
 */

import {
  PublicClientApplication,
  type AccountInfo,
  InteractionRequiredAuthError,
} from '@azure/msal-browser'
import {
  msalConfig,
  loginRequest,
  silentRequest,
  isLocalAuthBypassEnabled,
} from '@/config/authConfig'
import type { AuthUser } from '@/types'

class AuthService {
  private msalInstance: PublicClientApplication | null
  private initialized = false

  constructor() {
    if (isLocalAuthBypassEnabled()) {
      this.msalInstance = null
      this.initialized = true
      return
    }
    this.msalInstance = new PublicClientApplication(msalConfig)
  }

  /**
   * Initialize MSAL instance. Must be called before any other method.
   */
  async initialize(): Promise<void> {
    if (this.initialized) return
    if (!this.msalInstance) {
      this.initialized = true
      return
    }

    await this.msalInstance.initialize()

    // Handle redirect response from CIAM login/sign-up.
    try {
      const response = await this.msalInstance.handleRedirectPromise()
      if (response) {
        this.msalInstance.setActiveAccount(response.account)
      }
    } catch (error) {
      console.error('[Auth] Error handling redirect:', error)
    }

    this.initialized = true
  }

  /**
   * Get the current authenticated account
   */
  getAccount(): AccountInfo | null {
    if (!this.msalInstance) return null
    const accounts = this.msalInstance.getAllAccounts()
    if (accounts.length === 0) {
      return null
    }
    // Return the first account (most recent)
    return accounts[0]
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    return this.getAccount() !== null
  }

  /**
   * Initiate login flow (redirect)
   */
  async login(): Promise<void> {
    if (!this.msalInstance) return
    try {
      await this.msalInstance.loginRedirect(loginRequest)
    } catch (error) {
      console.error('[Auth] Login error:', error)
      throw error
    }
  }

  /**
   * Initiate logout flow
   */
  async logout(): Promise<void> {
    if (!this.msalInstance) return
    const account = this.getAccount()
    if (account) {
      await this.msalInstance.logoutRedirect({
        account,
      })
    }
  }

  /**
   * Get access token for API calls
   */
  async getAccessToken(): Promise<string | null> {
    if (!this.msalInstance || isLocalAuthBypassEnabled()) {
      return null
    }
    const account = this.getAccount()
    if (!account) {
      console.warn('[Auth] No account found for token acquisition')
      return null
    }

    try {
      // Try to acquire token silently
      const response = await this.msalInstance.acquireTokenSilent({
        ...silentRequest,
        account,
      })
      return response.accessToken
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        // Silent token acquisition failed — return null instead of redirecting
        // to avoid bouncing the user back to CIAM on first sign-up
        console.warn('[Auth] Silent token acquisition failed, proceeding without token')
        return null
      }
      console.error('[Auth] Token acquisition error:', error)
      return null
    }
  }

  /**
   * Extract user information from the account and token claims
   */
  async getCurrentUser(): Promise<AuthUser | null> {
    if (!this.msalInstance || isLocalAuthBypassEnabled()) {
      return null
    }
    const account = this.getAccount()
    if (!account) {
      return null
    }

    // Get claims from the account's ID token
    const claims = account.idTokenClaims as Record<string, unknown> | undefined

    // Try to get access token to include in user object
    let accessToken: string | undefined
    try {
      accessToken = (await this.getAccessToken()) || undefined
    } catch {
      // Token acquisition might fail, that's okay for user info
    }

    // Extract user info from claims
    // CIAM uses different claim names than B2C
    const user: AuthUser = {
      id: account.localAccountId || account.homeAccountId,
      email: (claims?.email as string) || 
             (claims?.preferred_username as string) || 
             account.username || 
             '',
      displayName: (claims?.name as string) || 
                   (claims?.given_name as string) || 
                   account.name || 
                   'User',
      // Custom attributes from Graph API enrichment
      customerId: (claims?.customerId as string) || 
                  (claims?.extension_customerId as string) || 
                  null,
      roles: extractRoles(claims),
      registrationComplete: Boolean(claims?.customerId || claims?.extension_customerId),
      accessToken,
    }

    return user
  }

  /**
   * Get the MSAL instance for advanced usage
   */
  getMsalInstance(): PublicClientApplication {
    if (!this.msalInstance) {
      throw new Error('MSAL is disabled when local auth bypass is enabled')
    }
    return this.msalInstance
  }
}

/**
 * Extract roles from token claims
 * Handles both array and comma-separated string formats
 */
function extractRoles(claims: Record<string, unknown> | undefined): string[] {
  if (!claims) return []

  // Try multiple possible claim names
  const rolesClaim = claims.roles || 
                     claims.extension_roles || 
                     claims['http://schemas.microsoft.com/ws/2008/06/identity/claims/role']

  if (!rolesClaim) return []

  if (Array.isArray(rolesClaim)) {
    return rolesClaim as string[]
  }

  if (typeof rolesClaim === 'string') {
    return rolesClaim.split(',').map((r: string) => r.trim()).filter(Boolean)
  }

  return []
}

// Export singleton instance
export const authService = new AuthService()
